import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Checkbox,
  FormControlLabel,
  useTheme,
  IconButton,
  Chip,
  useMediaQuery,
  AppBar,
  Toolbar,
  Card,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  TextField,
  Divider
} from '@mui/material';
import {
  Close,
  Star,
  MoreVert,
  Add,
  ArrowUpward,
  Notifications,
  Timeline,
  CandlestickChart,
  ArrowBack,
  ShowChart,
  TrendingUp,
  TrendingDown,
  Remove,
  StarBorder,
  AccountBalanceWallet
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";

// Import all order form components
import Limit from '../BybitTrade/Forms/Limit';
import Market from '../BybitTrade/Forms/Market';
import Conditional from '../BybitTrade/Forms/Conditional';
import Oco from '../BybitTrade/Forms/Oco';
import TpSl from '../BybitTrade/Forms/TpSl';
import Twap from '../BybitTrade/Forms/Twap';
import Iceberg from '../BybitTrade/Forms/Iceberg';
import ScaledOrder from '../BybitTrade/Forms/ScaledOrder';
import TrailingStop from '../BybitTrade/Forms/TrailingStop';
import ChaseLimitOrder from '../BybitTrade/Forms/ChaseLimitOrder';
import OrderBookMobile from '../BybitTrade/OrderBookMobile';

const BybitMobileTradingInterface = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Redux selectors
  const currentPair = useSelector((state) => state.currentPair.currentPair);
  const coinRates = useSelector((state) => state.coins.coinRates);
  const stocks = useSelector((state) => state.coins.stock);
  const pairType = useSelector((state) => state.currentPair.pairType);
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);

  // State
  const [mainTab, setMainTab] = useState(1); // 0: Charts, 1: Trade (default to Trade)
  const [tradingType, setTradingType] = useState('Spot'); // Spot or Futures (default to Spot)
  const [chartTab, setChartTab] = useState(0); // Chart sub-tabs
  const [timeframe, setTimeframe] = useState('15m');
  const [orderForm, setOrderForm] = useState('Scaled Order');
  const [orderType, setOrderType] = useState('Buy'); // Buy or Sell
  const [leverage, setLeverage] = useState('10x');
  const [crossType, setCrossType] = useState('Cross');
  const [coinDetails, setCoinDetails] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [postOnly, setPostOnly] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [bottomTab, setBottomTab] = useState(0);

  // Order book data
  const [asks, setAsks] = useState([]);
  const [bids, setBids] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  // Available order types based on trading type
  const orderTypes = {
    Spot: [
      'Limit',
      'Market',
      'TP/SL',
      'Conditional',
      'OCO',
      'Trailing Stop',
      'Iceberg',
      'TWAP',
      'Scaled Order'
    ],
    Futures: [
      'TWAP',
      'Conditional', 
      'Market',
      'Limit',
      'Iceberg',
      'Scaled Order',
      'Chase Limit Order'
    ]
  };

  const formatPrice = (amount) => parseFloat(amount || 0).toFixed(2);
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(0);
  };
  const formatBalance = (amount) => parseFloat(amount || 0).toFixed(2);
  const formatQuantity = (amount) => parseFloat(amount || 0).toFixed(4);

  // Render the appropriate order form based on selected type
  const renderOrderForm = () => {
    const commonProps = {
      orderType,
      balance: fundingBal,
      coinPrice: coinDetails?.priceUsd || coinDetails?.current_price || 0,
      currentPair,
      tradingType
    };

    switch (orderForm) {
      case 'Limit':
        return <Limit {...commonProps} />;
      case 'Market':
        return <Market {...commonProps} />;
      case 'Conditional':
        return <Conditional {...commonProps} />;
      case 'OCO':
        return <Oco {...commonProps} />;
      case 'TP/SL':
        return <TpSl {...commonProps} />;
      case 'TWAP':
        return <Twap {...commonProps} />;
      case 'Iceberg':
        return <Iceberg {...commonProps} />;
      case 'Scaled Order':
        return <ScaledOrder {...commonProps} />;
      case 'Trailing Stop':
        return <TrailingStop {...commonProps} />;
      case 'Chase Limit Order':
        return <ChaseLimitOrder {...commonProps} />;
      default:
        return <Limit {...commonProps} />;
    }
  };

  const calculate24hrChange = useCallback((coin) => {
    let pair = {};
    if (pairType === 'stocks') {
      stocks.forEach(stock => {
        if (stock.short_name === coin) {
          pair = stock;
        }
      });
    } else {
      coinRates.forEach(coinRater => {
        if (coinRater.symbol === coin) {
          pair = coinRater;
        }
      });
    }
    return pair;
  }, [pairType, stocks, coinRates]);

  // Improved order book with realistic price-volume relationships
  const generateOrderBook = useCallback(() => {
    const basePrice = parseFloat(coinDetails?.priceUsd || coinDetails?.current_price || 2.2623);
    setCurrentPrice(basePrice);
    
    // Generate asks (sell orders - red) with price-volume correlation
    const askOrders = [];
    for (let i = 1; i <= 8; i++) {
      // Smaller price increments for larger volumes (more realistic)
      const priceIncrement = tradingType === 'Spot' ? 
        (basePrice * 0.00005 * i) + (Math.random() * basePrice * 0.00002) :
        (basePrice * 0.0001 * i) + (Math.random() * basePrice * 0.00005);
      
      const price = basePrice + priceIncrement;
      
      // Higher volumes for prices closer to current price
      const volumeBase = 50000 - (i * 4000);
      const quantity = volumeBase + (Math.random() * 20000);
      const depth = Math.random() * 50 + 25;
      
      askOrders.push({
        price: price,
        quantity: quantity,
        total: price * quantity,
        depth: depth
      });
    }
    
    // Generate bids (buy orders - green) with price-volume correlation
    const bidOrders = [];
    for (let i = 1; i <= 8; i++) {
      // Smaller price decrements for larger volumes
      const priceDecrement = tradingType === 'Spot' ? 
        (basePrice * 0.00005 * i) + (Math.random() * basePrice * 0.00002) :
        (basePrice * 0.0001 * i) + (Math.random() * basePrice * 0.00005);
      
      const price = basePrice - priceDecrement;
      
      // Higher volumes for prices closer to current price
      const volumeBase = 50000 - (i * 4000);
      const quantity = volumeBase + (Math.random() * 20000);
      const depth = Math.random() * 50 + 25;
      
      bidOrders.push({
        price: price,
        quantity: quantity,
        total: price * quantity,
        depth: depth
      });
    }
    
    setAsks(askOrders.reverse());
    setBids(bidOrders);
      }, [coinDetails?.priceUsd, coinDetails?.current_price, tradingType]);

  // Handle bottom tab navigation
  const handleBottomTabNavigation = (tab) => {
    if (tab === 'Orders(0)') {
      navigate('/trade-history');
    } else if (tab === 'Assets') {
      navigate('/assets');
    }
  };

  // Memoized chart symbol - handle stocks vs crypto
  const chartSymbol = useMemo(() => {
    if (!currentPair || !coinDetails) return '';
    
    if (pairType === 'stocks') {
      // For stocks, use the exchange:short_name format
      return `${coinDetails.exch}:${coinDetails.short_name}`;
    } else {
      // For crypto, use BINANCE format
      return `BINANCE:${currentPair}USDT`;
    }
  }, [currentPair, pairType, coinDetails]);

  // Initialize coinDetails when currentPair changes
  useEffect(() => {
    if (currentPair) {
      setCoinDetails(calculate24hrChange(currentPair));
    }
  }, [currentPair, calculate24hrChange]);

  useEffect(() => {
    if (coinDetails) {
      generateOrderBook();
    }
  }, [coinDetails, generateOrderBook]);

  // Handle navigation
  const handleNavigation = (item) => {
    if (item === 'Convert') {
      navigate('/swap-coin');
    }
    // Other navigation items are now functional trading types
  };

  // Memoized TradingView Chart Component
  const MemoizedChart = useMemo(() => {
    if (!chartSymbol) return null;
    return (
      <AdvancedRealTimeChart
        key={chartSymbol} // Force remount only when symbol changes
        symbol={chartSymbol}
        interval="15"
        timezone="Etc/UTC"
        theme="dark"
        locale="en"
        style="1"
        hide_top_toolbar={true}
        enable_publishing={false}
        save_image={false}
        backgroundColor="#0B0E11"
        hide_side_toolbar={true}
        allow_symbol_change={false}
        isTransparent={true}
        height="100%"
        width="100%"
      />
    );
  }, [chartSymbol]);

  const topNavItems = ['Convert', 'Spot', 'Futures'];
  const timeframes = ['15m', '1h', '4h', '1D', 'More'];
  const chartTabs = ['Chart', 'Overview', 'Data', 'Feed'];
  const bottomTabs = ['Orders(0)', 'Assets'];

  if (!coinDetails) return null;

  const changePercent = parseFloat(coinDetails?.changePercent24Hr || coinDetails?.change_percentage || 0);
  const isPositive = changePercent >= 0;

  return (
    <Box sx={{ 
      height: '100vh', 
      bgcolor: '#0B0E11',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Top Navigation */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-around', 
        py: 1,
        borderBottom: '1px solid #1E2329'
      }}>
        {topNavItems.map((item, index) => (
          <Typography
            key={item}
            variant="body2"
            onClick={() => {
              if (item === 'Convert') {
                handleNavigation(item);
              } else if (item === 'Spot' || item === 'Futures') {
                setTradingType(item);
                // Reset order form to first available type for the trading type
                setOrderForm(orderTypes[item][0]);
              }
            }}
            sx={{ 
              color: (item === tradingType && item !== 'Convert') ? '#F7A600' : '#848E9C',
              fontWeight: (item === tradingType && item !== 'Convert') ? 600 : 400,
              fontSize: '13px',
              cursor: 'pointer',
              '&:hover': {
                color: '#F7A600'
              }
            }}
          >
            {item}
          </Typography>
        ))}
        <Box sx={{ color: '#F7A600' }}>
          <IconButton size="small" sx={{ color: '#F7A600' }}>
            <CandlestickChart fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Charts/Trade Toggle */}
      <Box sx={{ 
        display: 'flex',
        bgcolor: '#1E2329',
        m: 1,
        borderRadius: 1,
        p: 0.5
      }}>
        <Button
          onClick={() => setMainTab(1)}
          sx={{
            flex: 1,
            py: 1,
            bgcolor: mainTab === 1 ? '#F7A600' : 'transparent',
            color: mainTab === 1 ? '#000' : '#848E9C',
            borderRadius: 0.5,
            fontSize: '14px',
            fontWeight: mainTab === 1 ? 600 : 500,
            '&:hover': {
              bgcolor: mainTab === 1 ? '#F7A600' : 'rgba(247, 166, 0, 0.1)',
              color: mainTab === 1 ? '#000' : '#F7A600'
            }
          }}
        >
          Trade
        </Button>
        <Button
          onClick={() => setMainTab(0)}
          sx={{
            flex: 1,
            py: 1,
            bgcolor: mainTab === 0 ? '#F7A600' : 'transparent',
            color: mainTab === 0 ? '#000' : '#848E9C',
            borderRadius: 0.5,
            fontSize: '14px',
            fontWeight: mainTab === 0 ? 600 : 500,
            '&:hover': {
              bgcolor: mainTab === 0 ? '#F7A600' : 'rgba(247, 166, 0, 0.1)',
              color: mainTab === 0 ? '#000' : '#F7A600'
            }
          }}
        >
          Charts
        </Button>
      </Box>

      {/* Notification Banner removed as per request */}

      {/* Symbol Header */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
              {pairType === 'stocks' ? currentPair : `${currentPair}USDT`}
            </Typography>
            <Chip 
              label={`${isPositive ? '+' : ''}${changePercent.toFixed(2)}%`}
              size="small"
              sx={{ 
                bgcolor: isPositive ? '#0ECB81' : '#F6465D',
                color: 'white',
                fontSize: '11px',
                height: 20
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', color: 'white' }}>
              ${formatPrice(coinDetails?.priceUsd || coinDetails?.current_price || 0)}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: isPositive ? '#0ECB81' : '#F6465D', 
              fontSize: '12px', 
              fontWeight: 500 
            }}>
              {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
            </Typography>
          </Box>
        </Box>

        {mainTab === 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#848E9C', fontSize: '12px' }}>
              Funding Rate / Countdown
            </Typography>
            <Typography variant="body2" sx={{ color: '#0ECB81', fontSize: '12px' }}>
              0.0100% / 07:06:04
            </Typography>
          </Box>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        paddingBottom: mainTab === 1 ? '120px' : 0 // Add more space for bottom tabs and drawer in trade view
      }}>
        {mainTab === 0 ? (
          // Stable TradingView Chart
          <Box sx={{ height: '100%', p: 1 }}>
            {MemoizedChart}
          </Box>
        ) : (
          /* Trade View */
          <Box sx={{ 
            height: '100%', 
            display: 'flex',
            overflow: 'hidden' // Prevent overflow issues
          }}>
            {/* Left Side - Trading Form */}
            <Box sx={{ 
              width: '50%', 
              borderRight: '1px solid #1E2329',
              overflow: 'auto',
              maxHeight: '100%'
            }}>
              {/* Buy/Sell Toggle */}
              <Box sx={{ display: 'flex', p: 2, gap: 1 }}>
                <Button
                  variant={orderType === 'Buy' ? 'contained' : 'outlined'}
                  onClick={() => setOrderType('Buy')}
                  sx={{
                    flex: 1,
                    bgcolor: orderType === 'Buy' ? '#0ECB81' : 'transparent',
                    color: orderType === 'Buy' ? 'white' : '#0ECB81',
                    borderColor: '#0ECB81',
                    fontSize: '14px',
                    fontWeight: 600,
                    '&:hover': { 
                      bgcolor: orderType === 'Buy' ? '#0BB76D' : 'rgba(14, 203, 129, 0.1)'
                    }
                  }}
                >
                  Buy
                </Button>
                <Button
                  variant={orderType === 'Sell' ? 'contained' : 'outlined'}
                  onClick={() => setOrderType('Sell')}
                  sx={{
                    flex: 1,
                    bgcolor: orderType === 'Sell' ? '#F6465D' : 'transparent',
                    color: orderType === 'Sell' ? 'white' : '#F6465D',
                    borderColor: '#F6465D',
                    fontSize: '14px',
                    fontWeight: 600,
                    '&:hover': { 
                      bgcolor: orderType === 'Sell' ? '#E13B52' : 'rgba(246, 70, 93, 0.1)'
                    }
                  }}
                >
                  Sell
                </Button>
              </Box>

              {/* Order Type Selector */}
              <Box sx={{ px: 2, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={orderForm}
                    onChange={(e) => {
                      setOrderForm(e.target.value);
                      // Reset order form to first available type for the trading type if needed
                      if (!orderTypes[tradingType].includes(e.target.value)) {
                        setOrderForm(orderTypes[tradingType][0]);
                      }
                    }}
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#1E2329' },
                      '.MuiSvgIcon-root': { color: '#848E9C' }
                    }}
                  >
                    {orderTypes[tradingType].map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Render Selected Order Form */}
              {renderOrderForm()}
            </Box>

            {/* Right Side - Order Book */}
            <Box sx={{ 
              width: '50%', 
              p: 2,
              overflow: 'auto',
              maxHeight: '100%'
            }}>
              <OrderBookMobile price={parseFloat(coinDetails?.priceUsd || coinDetails?.current_price || 0)} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BybitMobileTradingInterface;